/**
 * Contents of this file were produced with a help of a code generation tool and
 * subsequently reviewed and edited by a human.
 *
 * While some of the code was created by an AI assisted service, manual adjustments
 * have been made to ensure correctness, readability, functionality and compliance
 * to Avalara's coding standards and guidelines. Any future modifications should
 * be approached with caution to avoid overwriting the manual changes.
 *
 * This source code is owned by Avalara Inc. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Proprietary and confidential.
 *
 * For more information, refer to the documentation or contact the development team.
 */

import store from "../app/store";
import axiosWithoutInterceptor from "../axiosWithoutInterceptor";
import getConfig from "../config";
import { buildApiV2Point5Url } from "./Utils";

// Config is retrieved lazily inside functions to support runtime configuration
const getAppEnv = () => getConfig().appEnv;
const QUEUE_SIZE_LIMIT = 20;
const API_TIMEOUT_MS = 2000;
const SESSION_CACHE_TTL = 5000;

let isAuthenticationInProgress = false;
let logQueue = [];
let isProcessingQueue = false;
let cachedSession = null;
let sessionCacheTime = 0;

const getCachedSession = () => {
    const now = Date.now();
    if (!cachedSession || now - sessionCacheTime > SESSION_CACHE_TTL) {
        sessionCacheTime = now;
        cachedSession = store.getState().session;
    }
    return cachedSession;
};

const executeLog = async (type, logLevel, params) => {
    try {
        let logMsg = "";
        const session = getCachedSession();

        if (!session?.isAuthenticated || !session?.userId) {
            return;
        }

        const pageUrl = window?.location?.href;
        const pathName = window?.location?.pathname;

        if (type === 1) {
            logMsg = `Error page rendered to user ${session.username} with id ${session.userId} for ${session.accountId} and ${session.activeCompany.id} `;
            logMsg += `on page ${pageUrl} with error: ${params?.message || "Unknown error"}`;
        } else if (type === 2) {
            logMsg = `JS Error for user ${session.username} with id ${session.userId} for ${session.accountId} and ${session.activeCompany.id} `;
            logMsg += `on page ${pageUrl} with error: ${params?.message || "Unknown error"}`;
        } else if (type === 3) {
            logMsg = `Upload Error for user ${session.username} with id ${session.userId} for ${session.accountId} and ${session.activeCompany.id} `;
            logMsg += `on page ${pathName} with error: ${params?.message || "Unknown error"}`;
        }

        await axiosWithoutInterceptor.post(
            buildApiV2Point5Url(`logger`),
            { logMessage: logMsg },
            { withCredentials: true, timeout: API_TIMEOUT_MS }
        );
    } catch (error) {
        if (getAppEnv() !== "prd" && getAppEnv() !== "sbx") {
            // eslint-disable-next-line no-console
            console.error("Logger failed:", error.message);
        }
    }
};

const processQueuedLogs = async () => {
    if (isProcessingQueue || logQueue.length === 0) {
        return;
    }

    isProcessingQueue = true;
    const logsToProcess = [...logQueue];
    logQueue = [];

    try {
        // Fire-and-forget: don't await, let each log complete independently
        logsToProcess.forEach(logData => {
            executeLog(logData.type, logData.logLevel, logData.params).catch(error => {
                if (getAppEnv() !== "prd" && getAppEnv() !== "sbx") {
                    // eslint-disable-next-line no-console
                    console.error("Failed to process queued log:", error.message);
                }
            });
        });
    } catch (error) {
        if (getAppEnv() !== "prd" && getAppEnv() !== "sbx") {
            // eslint-disable-next-line no-console
            console.error("Failed to process queued logs:", error.message);
        }
    } finally {
        isProcessingQueue = false;
    }
};

export const cleanupLogger = () => {
    cachedSession = null;
    sessionCacheTime = 0;
    logQueue = [];
    isProcessingQueue = false;
};

export const setAuthenticationInProgress = inProgress => {
    isAuthenticationInProgress = inProgress;
    cachedSession = null;
    sessionCacheTime = 0;

    if (inProgress) {
        isProcessingQueue = false;
    }

    if (!inProgress && logQueue.length > 0 && !isProcessingQueue) {
        processQueuedLogs().catch(error => {
            if (getAppEnv() !== "prd" && getAppEnv() !== "sbx") {
                // eslint-disable-next-line no-console
                console.error("Failed to process queued logs:", error.message);
            }
        });
    }
};

const shouldLogDuringAuth = (type, params) => {
    // React component errors
    if (type === 2) {
        return true;
    }

    // API errors
    if (type === 1) {
        const status = params?.status;
        return status !== 401;
    }

    // AuiUploader errors
    if (type === 3) {
        return true;
    }

    return true;
};

export const logger = async (type, logLevel, params) => {
    try {
        if (isAuthenticationInProgress) {
            if (shouldLogDuringAuth(type, params)) {
                logQueue.push({ type, logLevel, params });

                if (logQueue.length > QUEUE_SIZE_LIMIT) {
                    logQueue.shift();
                }
            }
            return;
        }

        // Fire and forget - don't await the log execution
        executeLog(type, logLevel, params).catch(error => {
            if (getAppEnv() !== "prd" && getAppEnv() !== "sbx") {
                // eslint-disable-next-line no-console
                console.error("Logger execution failed:", error.message);
            }
        });
    } catch (error) {
        // Handle validation errors gracefully
        if (getAppEnv() !== "prd" && getAppEnv() !== "sbx") {
            // eslint-disable-next-line no-console
            console.error("Logger validation failed:", error.message);
        }
    }
};

export default logger;
